import { getApiUrl, getAppPlatformUrl } from "@dashboard/config";
import { postConfigurationUpdate } from "@dashboard/envSettings/services/configurationApi";
import { PermissionEnum, useAppCreateMutation } from "@dashboard/graphql";
import { useEffect, useState } from "react";

export interface ThirdPartyApp {
  app_name: string;
  configurations: Record<string, any>;
  is_active: boolean;
}

export interface ThirdPartyIntegrations {
  internal: ThirdPartyApp[];
  external: ThirdPartyApp[];
}

const API_BASE_URL = getAppPlatformUrl("app-configurations");

// Static app definitions that should always be shown
const STATIC_APPS: ThirdPartyIntegrations = {
  internal: [
    {
      app_name: "dealer_locator",
      configurations: {},
      is_active: false,
    },
    {
      app_name: "checkout_questions",
      configurations: {},
      is_active: false,
    },
    {
      app_name: "checkout_terms_conditions",
      configurations: {},
      is_active: false,
    },
    {
      app_name: "shipping_restrictions",
      configurations: {},
      is_active: false,
    },
    {
      app_name: "will_call",
      configurations: {},
      is_active: false,
    },
  ],
  external: [
    {
      app_name: "google_analytics",
      configurations: {},
      is_active: false,
    },
    {
      app_name: "google_tag_manager",
      configurations: {},
      is_active: false,
    },
    {
      app_name: "google_adsense",
      configurations: {},
      is_active: false,
    },
    {
      app_name: "google_maps",
      configurations: {},
      is_active: false,
    },
  ],
};

export const useThirdPartyIntegrations = () => {
  const [data, setData] = useState<ThirdPartyIntegrations>(STATIC_APPS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [createApp] = useAppCreateMutation();

  // Function to create dealer_locator app via GraphQL
  // This is ONLY used for dealer_locator - Google apps are external services
  // that don't need to be created in the system, they just need configuration
  const createDealerLocatorApp = async (tenantUrl: string) => {
    try {
      const result = await createApp({
        variables: {
          input: {
            name: "Dealers",
            permissions: [
              PermissionEnum.MANAGE_CHANNELS,
              PermissionEnum.MANAGE_PRODUCTS,
              PermissionEnum.MANAGE_SETTINGS,
              PermissionEnum.MANAGE_SHIPPING,
            ],
          },
        },
      });

      if (result.data?.appCreate?.errors?.length === 0 && result.data?.appCreate?.authToken) {
        const authToken = result.data.appCreate.authToken;

        // Call POST endpoint to save configuration
        await postConfigurationUpdate({
          tenant: tenantUrl,
          app_name: "dealer_locator",
          configurations: {
            token: authToken,
          },
          is_active: true,
        });

        return true;
      } else {
        console.error("Failed to create dealer locator app:", result.data?.appCreate?.errors);

        return false;
      }
    } catch (err) {
      console.error("Error creating dealer locator app:", err);

      return false;
    }
  };

  useEffect(() => {
    const fetchIntegrations = async () => {
      try {
        setLoading(true);

        const TENANT_URL = getApiUrl();

        if (!TENANT_URL) {
          throw new Error("API_URL environment variable is not configured");
        }

        const response = await fetch(
          `${API_BASE_URL}/app/get-configuration?tenant=${encodeURIComponent(TENANT_URL)}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          },
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch third-party integrations: ${response.status}`);
        }

        const result: ThirdPartyApp[] = await response.json();

        // Ensure result is an array
        const safeResult = Array.isArray(result) ? result : [];

        // No automatic app creation here - only when user explicitly enables dealer_locator

        // Always merge API state with static apps - static apps will always show even if API returns empty arrays
        const mergedData: ThirdPartyIntegrations = {
          internal: STATIC_APPS.internal.map(staticApp => {
            const apiApp = safeResult.find(
              (app: ThirdPartyApp) => app.app_name === staticApp.app_name,
            );

            return {
              ...staticApp,
              is_active: apiApp?.is_active ?? false,
              configurations: apiApp?.configurations ?? {},
            };
          }),
          external: STATIC_APPS.external.map(staticApp => {
            const apiApp = safeResult.find(
              (app: ThirdPartyApp) => app.app_name === staticApp.app_name,
            );

            return {
              ...staticApp,
              is_active: apiApp?.is_active ?? false,
              configurations: apiApp?.configurations ?? {},
            };
          }),
        };

        setData(mergedData);
        setError(null);
      } catch (err) {
        console.error("Error fetching third-party integrations:", err);
        setError(err instanceof Error ? err.message : "Unknown error");
        // Keep static apps even on error
        setData(STATIC_APPS);
      } finally {
        setLoading(false);
      }
    };

    fetchIntegrations();
  }, [createApp]);

  return { data, loading, error, createDealerLocatorApp };
};
